import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Chip, Empty, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { currency, titleCase } from '../../utils/format';

const PROPERTY_FILTERS = [
  { key: '', label: 'All' },
  { key: 'dealType=rent', label: '🔑 For rent' },
  { key: 'dealType=sale', label: '🏷️ For sale' },
  { key: 'propertyType=flat', label: '🏢 Flats' },
  { key: 'propertyType=house', label: '🏡 Houses' },
  { key: 'propertyType=plot', label: '🌾 Plots' },
  { key: 'propertyType=shop', label: '🏪 Shops' },
];
const ANIMAL_FILTERS = [
  { key: '', label: 'All' },
  { key: 'animalType=cow', label: '🐄 Cows' },
  { key: 'animalType=buffalo', label: '🐃 Buffalo' },
  { key: 'animalType=goat', label: '🐐 Goats' },
  { key: 'animalType=dog', label: '🐕 Dogs' },
  { key: 'animalType=cat', label: '🐈 Cats' },
  { key: 'animalType=bird', label: '🦜 Birds' },
  { key: 'animalType=poultry', label: '🐔 Poultry' },
];

/** Browse property or animals. `kind` comes from the route. */
const ListingsPage = ({ kind: fixedKind }) => {
  const params = useParams();
  const kind = fixedKind || (params.kind === 'animal' ? 'animal' : 'property');
  const isProperty = kind === 'property';
  const isItem = kind === 'item';
  /* Three classifieds, one page: the url segment follows the kind. */
  const base = isProperty ? 'property' : isItem ? 'buy-sell' : 'animals';
  const navigate = useNavigate();
  const { location } = useApp();

  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = Object.fromEntries(new URLSearchParams(filter));
      if (location && location.latitude) {
        q.near = `${location.longitude},${location.latitude}`;
        q.radiusKm = 50;
      }
      const res = await api.listings[kind].list(q);
      setItems(res.data || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [kind, filter, location]);
  useEffect(() => { load(); }, [load]);

  const summary = (item) => {
    if (isProperty) {
      const p = item.property || {};
      return [p.bedrooms ? `${p.bedrooms} BHK` : titleCase(p.propertyType),
        p.areaValue ? `${p.areaValue} ${p.areaUnit}` : null,
        p.furnishing !== 'unfurnished' ? titleCase(p.furnishing) : null].filter(Boolean).join(' · ');
    }
    const a = item.animal || {};
    return [a.breed || titleCase(a.animalType),
      a.ageMonths ? `${Math.floor(a.ageMonths / 12)}y` : null,
      a.count > 1 ? `${a.count} nos` : null, a.vaccinated ? 'vaccinated' : null].filter(Boolean).join(' · ');
  };

  return (
    <>
      <PageHeader
        title={isProperty ? 'Property' : isItem ? 'Buy & sell' : 'Animals'}
        subtitle={isProperty
          ? 'Rent or buy homes, plots and shops'
          : isItem
            ? 'Second-hand things from people near you'
            : 'Cattle, pets and livestock near you'}
        actionLabel={isProperty ? 'Post a property' : isItem ? 'Sell something' : 'Sell an animal'}
        onAction={() => navigate(`/${base}/post`)}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {(isItem ? [{ key: '', label: 'Everything' }] : isProperty ? PROPERTY_FILTERS : ANIMAL_FILTERS).map((f) => (
          <Chip key={f.key || 'all'} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty emoji={isProperty ? '🏠' : '🐄'} title={`No ${isProperty ? 'property' : 'animals'} yet`}
          text="Be the first to post one - it takes a minute." />
      ) : (
        <div className="grid cols-2" style={{ gap: 14 }}>
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 40, 260)}>
              <button type="button" onClick={() => navigate(`/${base}/${item._id}`)}
                className="card press" style={{ display: 'flex', gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                {item.images && item.images[0] ? (
                  <img src={fileUrl(item.images[0])} alt="" style={{ width: 110, height: 110, borderRadius: 12, objectFit: 'cover', flex: '0 0 auto' }} />
                ) : (
                  <span style={{ width: 110, height: 110, borderRadius: 12, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, flex: '0 0 auto' }}>
                    {isProperty ? '🏠' : '🐄'}
                  </span>
                )}
                <span className="grow">
                  <strong style={{ display: 'block' }}>{item.title}{item.isFeatured ? ' ⭐' : ''}</strong>
                  <span className="t-tiny" style={{ display: 'block' }}>{summary(item)}</span>
                  <span className="t-tiny" style={{ display: 'block' }}>📍 {[item.city, item.district].filter(Boolean).join(', ')}</span>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
                    <strong style={{ color: 'var(--primary)', fontSize: 18 }}>{currency(item.price)}</strong>
                    {item.priceUnit !== 'total' ? <span className="t-tiny">{item.priceUnit}</span> : null}
                    {item.dealType === 'rent' ? <span className="t-tiny" style={{ color: 'var(--accent)', fontWeight: 800 }}>FOR RENT</span> : null}
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      )}
    </>
  );
};

export default ListingsPage;
